<template>
  <div class="boolean-search-layout">

    <div class="sidebar-section three-rows">
      <queries-sidebar
          class="links-sidebar"
          :class="isTag ? 'reverse-searches' : 'searches'"
          type="searches"
          :data="searchesData"
          :is-loading="searchesAreInitiated && searchesAreBusy"
          :url="`/clients/${client_id}/projects/${project_id}/searches`"
          :show-icon="true"
          @actionButtonClick="sidebarActionButtonClick('searches')"
      />

      <queries-sidebar
          class="links-sidebar"
          :class="isTag ? 'reverse-groups' : 'groups'"
          type="group_library"
          :data="groupsData"
          :is-loading="groupsAreInitiated && groupsAreBusy"
          :url="`/clients/${client_id}/projects/${project_id}/searches/group_library`"
          :is-group-type="true"
          title="Groups"
          :show-icon="true"
          @actionButtonClick="sidebarActionButtonClick('group_library')"
      />

      <queries-sidebar
          class="links-sidebar"
          :class="isTag ? 'reverse-tags-searches' : 'tagsSearches'"
          type="tag_library"
          :data="tagsSearchesData"
          :is-loading="tagsSearchesAreInitiated && tagsSearchesAreBusy"
          :url="`/clients/${client_id}/projects/${project_id}/tags`"
          :is-tag-search-type="true"
          title="Tags"
          :show-icon="true"
          @actionButtonClick="sidebarActionButtonClick('tags')"
      />
    </div>
    <section>
      <search-form
          :key="pageKey"
          v-if="!isBusy"
          :title="title"
          :resourceName="resourceName"
          :model="model"
          :dbModel="item"
          :detail-fields="detailFieldsList"
          :customValidators="customValidators"
          :filter-fields="filterFields"
          :details-fields-additions="['tagsearch', 'tag_library'].indexOf(this.resourceName.toLowerCase()) > -1 ? tagDetailsFieldsAdditions : detailsFieldsAdditions"
          :filters-fields-additions="filtersFieldsAdditions"
          :query-fields-additions="queryFieldsAdditions"
          :hide-filters="hideFilters"
          :errorMessage="errorMessage"
          :updateModel="updateModel"
          @submit="submit"
          @reset="resetQuery"
      />

      <div v-else class="text-center">
        <loader-inline />
      </div>

    </section>

    <div class="sidebar-section one-row">
      <aside>
        <last-activity-sidebar :resource-name="resourceName" />
      </aside>
    </div>

  </div>
</template>

<script>
// Plugins
import { mapActions, mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

// Components
import queriesSidebar from './sidebars/queries-sidebar.vue'
import lastActivitySidebar from './sidebars/last-activity-sidebar'
import searchForm from "./searches/SearchForm";
import loaderInline from '../loader-inline.vue'

// Mixins
import computedQueries from "../../mixins/boolean-search/computedQueries";
import modelShapes from "../../mixins/boolean-search/modelShapes";
import formsStoreData from "../../mixins/boolean-search/formsStoreData";
import notifyMessages from "../../mixins/boolean-search/notifyMessages";
import customValidation  from "../../mixins/forms/customValidation";

//Helpers
import { trimObjectValues } from '../../helpers/object';

//Config
import { query, searchTypes, multiSyntaxSearch, searchTermsCountLimit } from "../../config/bool-const";
import { CUSTOM_VALIDATIONS, INPUT_FIELDS, MEDIA_SUBTYPES } from "../../config/const";

export default {
  name: "SearchGroupFormPage",

  mixins: [ computedQueries, modelShapes, formsStoreData, notifyMessages, customValidation ],

  props: {
    resourceName: {
      type: String,
      required: true
    },
    item_id: { // prop from $router
      type: String,
    },
    item: {
      type: Object,
      default: () => {}
    },
    detailFields: {
      type: Array,
      default: () => ['name', 'multiSyntax', 'boolSearchType', 'boolDescription']
    },
    title: {
      type: String,
      required: true
    },
    url: {
      type: String,
      required: true
    },
    hideFilters: Boolean,
    isBusy: Boolean
  },

  async created() {
    this.detailFieldsList = [...this.detailFields];
    this.getMediaTypeOptions();
    this.resetQuery();
    this.mapValidationFunctions();
    this.getProjectSetup({ projectUid: this.$route.params.project_id })
  },

  data() {
    return {
      model: {},
      pageKey: 0,
      errorMessage: '',
      customFormValidation: CUSTOM_VALIDATIONS.boolean_detail,
      filterFields: [],
      detailFieldsList: []
    }
  },

  computed: {
    ...mapState({
      search: state => state.booleanSearch.search,
      group: state => state.booleanSearch.group,
      tagSearch: state => state.booleanSearch.tagSearch,
      groupBlocked: state => state.booleanSearch.group_blocked,
      projectSetupGeneral: state => state?.projectSetup?.projectSetup?.data?.general
    }),
    createSuccessMessage() { 
      if (['search', 'searches'].indexOf(this.resourceName.toLowerCase()) > -1) return this.$t('booleanSearch.search_create_success_message') 
      else if (['group', 'groupes'].indexOf(this.resourceName.toLowerCase()) > -1) return this.$t('booleanSearch.group_create_success_message')
      else return this.$t('booleanSearch.tagSearch_create_success_message')
    },
    updateSuccessMessage() { 
      if (['search', 'searches'].indexOf(this.resourceName.toLowerCase()) > -1) return this.$t('booleanSearch.search_update_success_message') 
      else if (['group', 'groupes'].indexOf(this.resourceName.toLowerCase()) > -1) return this.$t('booleanSearch.group_update_success_message')
      else return this.$t('booleanSearch.tagSearch_update_success_message')
    },
    isGroupBlocked() {
      return this.groupBlocked && this.groupBlocked.groups.length > 0 || this.groupBlocked.searches.length > 0
    },
    isTag(){
      return ['tag_library', 'tagSearch'].indexOf(this.resourceName) > -1
    },
    isGroup(){
      return ['group_library', 'group'].indexOf(this.resourceName) > -1
    }
  },

  methods: {
    updateModel(newValue, field) {
      if(!field) return;
      this.$set(this.model, field.name, newValue[field.name]);
    },

    takeQuerySnapShot(model) {
      Object.keys(model).forEach(key => {
        this.$set(this.model, key, model[key])
      })
    },

    resetQuery() {
      const data = cloneDeep({ ...this.item });

      if (['twitter', 'twitter_search_v3'].includes(data.search_type)) {
        const deepSearchLocation = Array.isArray(this.item.biolocations) && this.item.biolocations.length > 0 && typeof this.item.min_follower_biolocation === 'number';
        const minFollowerBiolocation = this.item.min_follower_biolocation || query.filters.min_follower_biolocation.values.min_follower_biolocation;
        data.deep_search_location = deepSearchLocation;
        data.min_follower_biolocation = minFollowerBiolocation;
      }

      this.takeQuerySnapShot(this.unwrapModel(data));
      this.pageKey++
    },

    resetQueryFilters () {
      const defaultFilters = this.unwrapModel({ filters: query.filters });
      const modelFilterNames = this.filterFields.map(f => this.filtersFieldsAdditions[f].name);
      const otherFilters = Object.keys(this.model).filter(k => k in defaultFilters && modelFilterNames.indexOf(k) === -1);
      let model = { ...this.model };

      if (!this.model.id) {
        // Reset current filter values to their default values, to prevent values from overflowing to other searches
        model = {
          ...this.model,
          ...modelFilterNames.reduce((acc, val) => ({ ...acc, [val]: defaultFilters[val] }), {})
        }
      }

      // Remove model filters that are not relevant to the current search type
      for (let i = 0; i < otherFilters.length; i++) {
        const f = otherFilters[i];
        delete model[f];
      }

      this.model = { ...model }
    },

    submit() {
      let model = trimObjectValues(this.model);
      // if US is not selected, remove any states being sent to the API
      if(this.disableStates) model = {...model, ...{ include_states: [], exclude_states: []}}

      if(this.model.id) {
        return this.editResource({key: this.resourceName, model, url: this.url })
          .then(() => {
            if((!this[this.resourceName].errorMessage || this[this.resourceName].errorMessage === '') && !this.isGroupBlocked) {
              this.errorMessage = '';
              this.notifyStoreSuccess(this.updateSuccessMessage);
            }
            else if(this[this.resourceName].errorMessage) {
              this.errorMessage = this[this.resourceName].errorMessage;
            }
          })
          .catch(() => this.notifyStoreError());
      }

      //Clear extra queries before sending to API
      if((!this.isTag && !this.isGroup) || model.is_multi) {
        const searchQueries = model.is_multi ? multiSyntaxSearch.queries : searchTypes.find(s => s.value === this.model.search_type)?.queries;
        const queryKeys = searchQueries.map(q => INPUT_FIELDS[q].queryKey);
        Object.keys(query.queries).map(q => {
          if(model.hasOwnProperty(q) && !queryKeys.includes(q)) model[q] = '';
        });
      }


      this.createResource({key: this.resourceName, model, url: this.url })
        .then(({ id }) => {
          let singleKey 
          if (this.resourceName === 'searches') singleKey = 'search'
          else if (this.resourceName === 'group_library') singleKey = 'group'
          else singleKey = 'tagSearch'
          if(id && !this[singleKey].errorMessage || this[singleKey].errorMessage === '') {
            this.errorMessage = '';
            let url = `/clients/${this.client_id}/projects/${this.project_id}/`
            if (this.resourceName === 'searches') url += `searches/${id}`
            else if (this.resourceName === 'group_library') url += `searches/${this.resourceName}/${id}`
            else url += `tags/${id}`
            this.$router.push(url)
            this.notifyStoreSuccess(this.createSuccessMessage)
          }
          else if(this[singleKey].errorMessage) {
            this.errorMessage = this[singleKey].errorMessage;
          }
        })
        .catch(() => this.notifyStoreError())
    },

    sidebarActionButtonClick(source) {
      const searchesPath = `/clients/${this.client_id}/projects/${this.project_id}/searches/create`;
      const groupsPath = `/clients/${this.client_id}/projects/${this.project_id}/searches/group_library/create`;
      const tagsSearchesPath = `/clients/${this.client_id}/projects/${this.project_id}/tags/create`;

      if(source === 'searches' && this.$route.path !== searchesPath) return this.$router.push(searchesPath)
      if(source === 'group_library' && this.$route.path !== groupsPath) return this.$router.push(groupsPath)
      if(source === 'tags' && this.$route.path !== tagsSearchesPath) return this.$router.push(tagsSearchesPath)
    },

    getMediaTypeOptions(searchType) {
      if(!searchType && !this.model.search_type) return;
      if(!searchType) searchType = this.model.search_type;
      
      let fields;
      if(this.model.is_multi && Array.isArray(searchType)) {
        fields = [...multiSyntaxSearch.fields];
      }
      else {
        fields = [...searchTypes.find(st => st.value === searchType).fields];
      }
      
      if (fields && fields.indexOf('includeMediaSubtypes') !== -1) {
        this.getMediaTypes({key: 'media_types', url: 'media_types', extraFilters: {type: searchType || 'online'}});
      }

      
      if (['twitter', 'twitter_search_v3'].includes(this.selectedSearchType.value) && fields && fields.indexOf('biolocations') > -1) {
        if (Array.isArray(this.model.biolocations) && this.model.biolocations.length > 0) {
          fields.push('deepSearchLocation');
        } else {
          fields = fields.filter(f => ['deepSearchLocation', 'locations', 'minFollowerBiolocation'].indexOf(f) === -1);
        }
      }

      if (['twitter', 'twitter_search_v3'].includes(this.selectedSearchType.value) && fields && fields.indexOf('deepSearchLocation') > -1) {
        const extraFields = ['locations', 'minFollowerBiolocation'];
        if (this.model.deep_search_location) {
          fields.push(...extraFields);
        } else {
          fields = fields.filter(f => extraFields.indexOf(f) === -1);
        }
      }

      this.filterFields = fields;
      
      // Update detailFieldsList based on search type (similar to filterFields)
      this.detailFieldsList = [...this.detailFields];
      if (searchType === 'carma_broadcast' && !this.detailFieldsList.includes('apply_cbs_outlet_timeframes')) {
        this.detailFieldsList.push('apply_cbs_outlet_timeframes');
      } else if (searchType !== 'carma_broadcast' && this.detailFieldsList.includes('apply_cbs_outlet_timeframes')) {
        this.detailFieldsList = this.detailFieldsList.filter(f => f !== 'apply_cbs_outlet_timeframes');
      }
    },

    boolNameValidation(model) { // do not allow double quotes and backslash
      const name = model.name
      if(!name) return
      const re =  new RegExp(/"|\\/i)
      return re.test(name)
    },

    searchTermsValidation (model) {
      const { must_include_terms = [], should_include_terms = [] } = this.model;
      const invalid = must_include_terms.length + should_include_terms.length > searchTermsCountLimit;
      return invalid;
    },

    ...mapActions({
      createResource: 'booleanSearch/createResource',
      editResource: 'booleanSearch/editResource',
      getMediaTypes: 'booleanSearch/searchGenericPaginatedResource',
      getProjectSetup: "projectSetup/getprojectSetup",
    })
  },

  watch: {
    selectedSearchType: {
      handler(newVal, oldValue) {
        if(!isEqual(newVal, oldValue)) {
          if(!isEmpty(oldValue) && !this.model.id) {
            this.pageKey++
          }
          
          let searchTypes;
          if(!this.model.is_multi) {
            searchTypes = newVal.value;
          
          if(newVal.value === 'online' && !this.model.id) {
            this.model.media_subtypes = [MEDIA_SUBTYPES['WEBSITE'], MEDIA_SUBTYPES['BLOG']];
          }

            if(['twitter', 'twitter_search_v3'].includes(newVal.value)) {
              const langCodes = this.resolvedLanguages.map(l => l.alpha_2_code);
              this.model.include_languages = (this.model.include_languages || []).filter(key => langCodes.includes(key));
              this.model.exclude_languages = (this.model.exclude_languages || []).filter(key => langCodes.includes(key));
            }
          }
          
          this.getMediaTypeOptions(searchTypes);
          this.resetQueryFilters();
        }
      }
    },
    toggleMultiSyntax: {
      handler(newVal, oldValue) {
        if(oldValue === undefined) return;

        let newSearchType;
        if (newVal === true) {
          newSearchType = this.selectedSearchType.allowMulti ? [this.selectedSearchType.value] : [query.search_type];
        }
        else {
          newSearchType = this.selectedSearchType && this.selectedSearchType.length ? this.selectedSearchType[0].value : query.search_type;
        }
        this.model.search_type = newSearchType;
      }
    },
    'model.biolocations': {
      handler (value) {
        if (['twitter', 'twitter_search_v3'].includes(this.selectedSearchType.value)) {
          this.getMediaTypeOptions(this.selectedSearchType.value);
        }
        
      }
    },
    'model.search_type': {
      handler(newVal) {   
        if (newVal) {
          this.getMediaTypeOptions(newVal);
        }
      }
    },
    'model.deep_search_location': {
      handler (value) {
        if (['twitter', 'twitter_search_v3'].includes(this.selectedSearchType.value)) {
          this.getMediaTypeOptions(this.selectedSearchType.value);
        }
      }
    }
  },

  components: {
    queriesSidebar,
    lastActivitySidebar,
    searchForm,
    loaderInline
  }
}
</script>
